import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { Radio, DatePicker, Tooltip } from 'antd'
import { createSimpleStore } from 'react-simple-reducer'
import dayjs from 'dayjs'
import Axios from 'axios'
import { EvolucaoDiariaTable } from './EvolucaoDiariaTable'

const { RangePicker } = DatePicker

interface DayRow {
  date: string
  count: number
  valorNewDriverSum: number
  isWeekend: boolean
  isHoliday: boolean
  holidayNames: string[]
}

type Tipo = 'todos' | 'venda_direta' | 'parceiros'
type Modo = 'pagamentos' | 'em_aberto'

const BiDiarioStore = createSimpleStore(
  {
    tipo: 'todos' as Tipo,
    modo: 'pagamentos' as Modo,
    dateRange: [currentMonthStart(), today()] as [string, string],
    loading: false as boolean,
    days: [] as DayRow[],
    error: null as string | null,
  },
  {
    setTipo: (state, tipo: Tipo) => ({ ...state, tipo }),
    setModo: (state, modo: Modo) => ({ ...state, modo }),
    setDateRange: (state, dateRange: [string, string]) => ({ ...state, dateRange }),
    fetchStarted: (state) => ({ ...state, loading: true, error: null }),
    fetchSuccess: (state, days: DayRow[]) => ({ ...state, days, loading: false }),
    fetchFailed: (state, error: string) => ({ ...state, loading: false, error }),
  },
  {
    thunks: {
      fetchData(tipo: Tipo, modo: Modo, startDate: string, endDate: string) {
        return async (dispatch: any) => {
          dispatch(BiDiarioStore.actions.fetchStarted())
          try {
            const { data } = await Axios.get('/matriculas/bi/diario', {
              params: { tipo, modo, startDate, endDate },
            })
            dispatch(BiDiarioStore.actions.fetchSuccess(data.days))
          } catch {
            dispatch(BiDiarioStore.actions.fetchFailed('Erro ao carregar dados'))
          }
        }
      },
    },
  }
)

export function BiEvolucaoDiaria() {
  const init = useCallback((dispatch: any) => {
    dispatch(BiDiarioStore.thunks.fetchData('todos', 'pagamentos', currentMonthStart(), today()))
  }, [])

  return (
    <BiDiarioStore.Provider init={init}>
      <ControlsRow>
        <Controls>
          <ModoToggle />
          <TipoToggle />
          <DateRangePickerControl />
        </Controls>
        <MeansSummary />
      </ControlsRow>
      <EvolucaoDiariaContent />
    </BiDiarioStore.Provider>
  )
}

function ModoToggle() {
  const { modo, tipo, dateRange } = BiDiarioStore.useState()
  const dispatch = BiDiarioStore.useDispatch()

  function handleChange(newModo: Modo) {
    dispatch(BiDiarioStore.actions.setModo(newModo))
    dispatch(BiDiarioStore.thunks.fetchData(tipo, newModo, dateRange[0], dateRange[1]))
  }

  return (
    <Radio.Group
      value={modo}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Pagamentos', value: 'pagamentos' },
        { label: 'Matrículas em Aberto', value: 'em_aberto' },
      ]}
    />
  )
}

function TipoToggle() {
  const { tipo, modo, dateRange } = BiDiarioStore.useState()
  const dispatch = BiDiarioStore.useDispatch()

  function handleChange(newTipo: Tipo) {
    dispatch(BiDiarioStore.actions.setTipo(newTipo))
    dispatch(BiDiarioStore.thunks.fetchData(newTipo, modo, dateRange[0], dateRange[1]))
  }

  return (
    <Radio.Group
      value={tipo}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Todos', value: 'todos' },
        { label: 'Venda Direta', value: 'venda_direta' },
        { label: 'Parceiros', value: 'parceiros' },
      ]}
    />
  )
}

function DateRangePickerControl() {
  const { dateRange, tipo, modo } = BiDiarioStore.useState()
  const dispatch = BiDiarioStore.useDispatch()

  function handleChange(dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) {
    if (!dates || !dates[0] || !dates[1]) return
    const start = dates[0].format('YYYY-MM-DD')
    const end = dates[1].format('YYYY-MM-DD')
    dispatch(BiDiarioStore.actions.setDateRange([start, end]))
    dispatch(BiDiarioStore.thunks.fetchData(tipo, modo, start, end))
  }

  return (
    <RangePicker
      value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
      onChange={handleChange}
      disabledDate={(d) => d.isAfter(dayjs(), 'day')}
      format="DD/MM/YYYY"
      allowClear={false}
    />
  )
}

function EvolucaoDiariaContent() {
  const { days, loading } = BiDiarioStore.useState()
  return <EvolucaoDiariaTable days={days} loading={loading} />
}

function MeansSummary() {
  const { days } = BiDiarioStore.useState()

  const segments = [
    { label: 'Dias úteis', rows: days.filter((d) => !d.isWeekend && !d.isHoliday) },
    { label: 'FDS / Feriados', rows: days.filter((d) => d.isWeekend || d.isHoliday) },
    { label: 'Todos', rows: days },
  ]

  return (
    <MeansList>
      {segments.map(({ label, rows }) => (
        <SegmentCard key={label} label={label} rows={rows} />
      ))}
    </MeansList>
  )
}

function SegmentCard({ label, rows }: { label: string; rows: DayRow[] }) {
  const counts = rows.map((r) => r.count)
  const revenues = rows.map((r) => r.valorNewDriverSum)

  const stats = [
    {
      label: 'Média',
      tooltip:
        'Soma de todos os valores dividida pelo número de dias. Exemplo: dias com 10, 20 e 30 matrículas → média = (10+20+30) ÷ 3 = 20.',
      count: `${mean(counts).toFixed(1)} mat.`,
      revenue: fmtBRL(mean(revenues)),
    },
    {
      label: 'Mediana',
      tooltip:
        'O valor central quando os dias são ordenados do menor ao maior. Exemplo: 10, 20, 30 → mediana = 20. Com 10, 20, 30 e 40 → mediana = (20+30) ÷ 2 = 25.',
      count: `${median(counts).toFixed(1)} mat.`,
      revenue: fmtBRL(median(revenues)),
    },
    {
      label: 'Total',
      tooltip: null,
      count: `${counts.reduce((a, b) => a + b, 0)} mat.`,
      revenue: fmtBRL(revenues.reduce((a, b) => a + b, 0)),
    },
  ]

  return (
    <MeansCard>
      <CardGrid>
        <CardSegmentLabel>{label}</CardSegmentLabel>
        <CardColHeader>Matrículas</CardColHeader>
        <CardColHeader>Receita</CardColHeader>
        {stats.map(({ label: statLabel, tooltip, count, revenue }) => (
          <React.Fragment key={statLabel}>
            <CardStatLabel>
              {tooltip ? (
                <Tooltip title={tooltip}>
                  <TooltippedStat>{statLabel}</TooltippedStat>
                </Tooltip>
              ) : (
                statLabel
              )}
            </CardStatLabel>
            <CardValue>{count}</CardValue>
            <CardValue>{revenue}</CardValue>
          </React.Fragment>
        ))}
      </CardGrid>
    </MeansCard>
  )
}

function mean(values: number[]): number {
  return values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length
}

function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

function fmtBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value)
}

const ControlsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`

const MeansList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const MeansCard = styled.div`
  background: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 5px 12px;
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  gap: 2px 16px;
  align-items: center;
`

const CardSegmentLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  padding-right: 8px;
`

const CardColHeader = styled.div`
  font-size: 10px;
  color: #9ca3af;
  text-align: right;
  white-space: nowrap;
`

const CardStatLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
`

const TooltippedStat = styled.span`
  border-bottom: 1px dotted #9ca3af;
  cursor: help;
`

const CardValue = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: right;
  white-space: nowrap;
`

function currentMonthStart(): string {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-CA')
}

function today(): string {
  return new Date().toLocaleDateString('en-CA')
}
